import { fetchData, fetchCheckout } from '../utils/async';
import { getAllCookies, addClass } from '../utils/dom';
import { deepCompare } from '../utils/object';
import { fieldValidation } from '../utils/string';

export const SUBSCRIBED = 'SUBSCRIBED';
export const SUBSCRIBED_PRODUCT_ALERT = 'SUBSCRIBED_PRODUCT_ALERT';
export const SUBSCRIBED_ERROR = 'SUBSCRIBED_ERROR';
export const RECENTLY_VIEWED = 'RECENTLY_VIEWED';
export const LIST_ITEM_CACHE = 'LIST_ITEM_CACHE';
export const ADD_VOUCHER = 'ADD_VOUCHER';
export const VOUCHER_INPUT = 'VOUCHER_INPUT';
export const VOUCHER_ERROR = 'VOUCHER_ERROR';
export const ADDED_TO_BAG = 'ADDED_TO_BAG';
export const ADD_TO_BAG_ERROR = 'ADD_TO_BAG_ERROR';
export const SELECT_SIZE = 'SELECT_SIZE';
export const SELECT_QUANTITY = 'SELECT_QUANTITY';
export const UPDATE_SELECTION_CACHE = 'UPDATE_SELECTION_CACHE';
export const UPDATE_SELECTION = 'UPDATE_SELECTION';
export const UPDATE_SELECTION_ERROR = 'UPDATE_SELECTION_ERROR';
export const UPDATE_ADDITIONAL_INFORMATION = 'UPDATE_ADDITIONAL_INFORMATION';
export const UPDATE_BUDBEE = 'UPDATE_BUDBEE';
export const GET_ADDRESS = 'GET_ADDRESS';
export const UPDATE_ADDRESS = 'UPDATE_ADDRESS';
export const UPDATE_ERRORS = 'UPDATE_ERRORS';
export const DELIVERY_PROCEED = 'DELIVERY_PROCEED';
export const KLARNA_LOADED = 'KLARNA_LOADED';
export const KLARNA_UNLOADED = 'KLARNA_UNLOADED';
export const ADYEN_INITIATED = 'ADYEN_INITIATED';
export const STRIPE_INITIATED = 'STRIPE_INITIATED';
export const PAYMENT_UPDATING = 'PAYMENT_UPDATING';
export const SELECTION_ERROR = 'SELECTION_ERROR';
export const SELECTION_ERROR_STOCK = 'SELECTION_ERROR_STOCK';
export const ENRICH_PRODUCTS = 'ENRICH_PRODUCTS';
export const INITIATED_SCRIPT = 'INITIATED_SCRIPT';
export const BOOLEAN_CHANGE = 'BOOLEAN_CHANGE';
export const PAYMENT_RETURN = 'PAYMENT_RETURN';
export const DESTROY_SELECTION = 'DESTROY_SELECTION';

export const UPDATE_ACCESS_TOKEN = 'UPDATE_ACCESS_TOKEN';

export const clientInit = () => ({ type: 'CLIENT_INIT' });
export const retrieveProductCache = () => ({ type: 'RETRIEVE_PRODUCT_CACHE' });
export const selectSize = (product, item, quantity = 1) => ({ type: SELECT_SIZE, product, item, quantity });
export const addRecentlyViewed = (product) => ({ type: RECENTLY_VIEWED, product });
export const updateListItemCache = (productId, listName) => ({ type: LIST_ITEM_CACHE, productId, listName });

export const subscribeKlaviyo = (email) => (dispatch) => (
	fetchData('klaviyo/subscribe', { email }).then((response) => {
		if (response?.errors) dispatch({ type: SUBSCRIBED_ERROR });
		return dispatch({ type: SUBSCRIBED, data: response });
	}).catch((err) => console.log(err))
);

export const productAlert = ({ email, selected }) => (dispatch) => (
	fetchData('klaviyo/alert', { email, variant: selected }, 'POST').then((result) => {
		dispatch({ type: SUBSCRIBED_PRODUCT_ALERT, selected });
		return result;
	}).catch((err) => console.log('DEBUG: error when subscribing to newsletter ', err))
);

/* Create new checkout or add to checkout with email address. */
export const subscribeToNewsletter = (email) => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	const { country } = getState().environment.setup;

	if (!token) return dispatch(subscribeKlaviyo(email)).then(() => dispatch(createCart(email)));

	const variables = {
		cartId: token,
		buyerIdentity: {
			email,
		},
	};

	return dispatch(subscribeKlaviyo(email)).then(() => (
		fetchCheckout('cartBuyerIdentityUpdate', variables, country)
	));
};

export const updateCartAttributes = ({ token, conversionString, cookieConsent, country }) => {
	const variables = {
		cartId: token,
		attributes: [
			{ key: 'cookieConsent', value: String(cookieConsent) },
			{ key: 'hostName', value: window.location.hostname },
			...(conversionString ? [{ key: 'conversionTracker', value: conversionString }] : []),
		],
	};

	return fetchCheckout('cartAttributesUpdate', variables, country).then((response) => response.data.cartAttributesUpdate.cart.id);
};

export const createCart = ({ email = false, lines = [], token = false }) => (dispatch, getState) => {
	const { cookies, country } = getState().environment.setup;
	const conversionTracker = getAllCookies('_');
	const conversionString = Object.keys(conversionTracker).reduce((acc, c) => `${acc};${c}=${conversionTracker[c]}`, '').slice(1);
	const cookieConsent = Array.isArray(getState().environment.cookieConsent) ? getState().environment.cookieConsent.join(',') : getState().environment.cookieConsent;

	if (!token) token = getState().checkout.selection.token;
	if (token && !email && lines.length === 0) {
		// Check if cookies have changed since load. If changed then update selection attributes.
		const trackingCookies = Object.keys(cookies).filter((c) => c.indexOf('_') === 0).reduce((acc, c) => ({ ...acc, [c]: cookies[c] }), {});
		if (!deepCompare(conversionTracker, trackingCookies)) {
			return updateCartAttributes({ token, conversionString, cookieConsent, country });
		}
		return new Promise((resolve) => resolve(token));
	}

	const variables = {
		input: {
			buyerIdentity: {
				countryCode: country,
			},
			attributes: [
				{ key: 'cookieConsent', value: String(cookieConsent) },
				{ key: 'hostName', value: window.location.hostname },
				...(conversionTracker ? [{ key: 'conversionTracker', value: conversionString }] : []),
			],
		},
	};
	if (email) variables.input.email = email;
	if (lines.length > 0) variables.input.lines = lines;

	return fetchCheckout('cartCreate', variables, country).then((response) => {
		dispatch({ type: UPDATE_SELECTION, data: response.data.cartCreate.cart });
		return response.data.cartCreate.cart.id;
	});
};

export const updateCheckoutAddress = () => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	const { countries, country } = getState().environment.setup;

	const addressVariables = {
		checkoutId: token,
		shippingAddress: Object.keys(getState().checkout.payment.address).reduce((acc, f) => {
			if (['termsAndConditions', 'newsletter', 'email', 'newsletterNotifyMe'].indexOf(f) !== -1) return acc;
			let value = getState().checkout.payment.address[f];
			if (f === 'country') value = countries[value].name;
			return {
				...acc,
				[f]: value,
			};
		}, {}),
	};

	return fetchCheckout('checkoutShippingAddressUpdate', addressVariables, country).then((response) => {
		dispatch({ type: UPDATE_SELECTION, data: response.data.checkoutShippingAddressUpdateV2.checkout });
		const { checkoutUserErrors = [] } = response.data.checkoutShippingAddressUpdateV2;

		if (checkoutUserErrors.length > 0) {
			const errors = [];
			checkoutUserErrors.forEach((checkoutUserError) => {
				errors[errors.length] = {
					group: checkoutUserError.field[0],
					id: checkoutUserError.field[1],
					message: checkoutUserError.message,
				};
			});

			return new Promise((resolve, reject) => reject({ checkoutUserErrors: errors }));
		}

		return response.data.checkoutShippingAddressUpdateV2.checkout.id;
	}).then((response) => {
		const emailVariables = {
			checkoutId: response,
			email: getState().checkout.payment.address.email,
		};
		const { country: updatedCountry = '' } = getState().environment.setup;

		return fetchCheckout('checkoutEmailUpdate', emailVariables, updatedCountry);
	});
};

export const addToBag = (lines = [], cachedItems, category) => (dispatch, getState) => {
	const { country } = getState().environment.setup;
	addClass(document.querySelector('html'), 'u_pageIsScrollingUp');

	return (
		dispatch(createCart({})).then((cartId) => fetchCheckout('cartLinesAdd', { cartId, lines }, country)).then((response) => {
			if (response.data.cartLinesAdd.userErrors.length > 0) return dispatch({ type: ADD_TO_BAG_ERROR });
			dispatch({ type: UPDATE_SELECTION_CACHE, lines, cachedItem: cachedItems, category });

			setTimeout(() => {
				dispatch({ type: ADDED_TO_BAG, open: true });
			}, 500);

			setTimeout(() => {
				dispatch({ type: ADDED_TO_BAG, open: false });
			}, 4000);

			return dispatch({ type: UPDATE_SELECTION, data: response.data.cartLinesAdd.cart, addedToBag: true });
		}).catch((err) => console.log('DEBUG: err', err))
	);
};

export const changeQuantity = (lines = [], cachedItem, category) => (dispatch, getState) => {
	const { country } = getState().environment.setup;

	return (
		dispatch(createCart({})).then((cartId) => fetchCheckout('cartLinesUpdate', { cartId, lines }, country)).then((response) => {
			dispatch({ type: UPDATE_SELECTION_CACHE, lines, cachedItem, category });

			return dispatch({ type: UPDATE_SELECTION, data: response.data.cartLinesUpdate.cart });
		}).catch((err) => console.log('DEBUG: err', err))
	);
};

export const updateCart = () => (dispatch, getState) => {
	const { items = [], token = false } = getState().checkout.selection;

	// If checkout never created then don't do anything.
	if (items.length === 0 && !token) return new Promise((resolve) => resolve());

	// Re-create a new basket with the current items.
	// TODO: Check that the items are available in current market.
	const lines = items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity }));

	return dispatch(createCart({ lines, token: 1 }));
};

export const changeAddressInput = (group, id, value) => ({ type: UPDATE_ADDRESS, group, id, value });
export const booleanChange = (id) => ({ type: BOOLEAN_CHANGE, id });

export const checkAddressInputErrors = (group, id, value) => {
	const fieldType = id === 'email' ? 'email' : 'text';
	const error = fieldValidation(value, fieldType);

	return ({ type: UPDATE_ERRORS, group, id, error });
};

export const enrichProducts = (params) => ({ type: ENRICH_PRODUCTS, ...params });

export const destroySelection = () => (dispatch, getState) => {
	const { country, countries } = getState().environment.setup;
	const defaultCountrySettings = countries[country];
	// Clear possible campaign market/pricelist to keep in sync with Centra.
	dispatch({ type: 'UPDATE_COUNTRY', country, market: parseInt(defaultCountrySettings.market, 10), pricelist: parseInt(defaultCountrySettings.pricelist, 10) });
	dispatch({ type: 'RETRIEVE_PRODUCT_CACHE' });

	dispatch({ type: DESTROY_SELECTION });
};

// ACCOUNT

export const login = () => (dispatch, getState) => {
	const { password, email } = getState().checkout.payment;
	const { language, country } = getState().environment.setup;

	if (fieldValidation(password, 'password') && fieldValidation(email, 'email')) {
		return dispatch({ type: 'LOGIN_ERROR' });
	}

	const variables = {
		input: {
			email,
			password,
		},
	};

	return fetchCheckout('accountLogin', variables, country).then((response) => {
		dispatch({ type: UPDATE_ACCESS_TOKEN, data: response.data.customerAccessToken });
		return dispatch({ type: 'ACCOUNT', payload: { language, path: 'account' } });
	});
};
